import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { ReauthenticationService } from '../services/reauthentication.service';
import { toApiError } from '../utils/api-error.utils';

/**
 * Turns every failed HTTP request into an `ApiError` (models/api-error.models.ts), so services and
 * components branch on `isNotFound`, `isConflict` and the rest instead of each reading status
 * codes and response bodies their own way.
 *
 * Registered first in `withInterceptors`, which makes it the outermost: on the way back it runs
 * after `authInterceptor`, closest to the subscriber. Only an `HttpErrorResponse` is converted.
 * Anything else — an MSAL rejection out of `authInterceptor`'s token acquisition, say — passes
 * through untouched so its error code survives; handlers that call `toApiError` still cope with it.
 *
 * A 401 from the API also sends the user to sign in again (`ReauthenticationService`, which
 * guards against a redirect loop and does nothing on the login page). `/api/auth/login` is left
 * out: it is the sign-in call itself, and the login page reports its failure. `/api/auth/me` is
 * not — the route guards call it on every page, so its 401 is how a deep link opened with a stale
 * session finds out.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // One console record per failure, with what ApiError does not carry (the raw body). The
      // per-service handlers this replaced each logged their own.
      console.error('HTTP request failed:', {
        status: error.status,
        url: error.url,
        body: error.error,
      });

      const apiError = toApiError(error);
      if (
        apiError.isUnauthenticated &&
        triggersReauthentication(request.url, error.error)
      ) {
        // Resolved here rather than per request: almost no request needs it, and it brings
        // AuthService (MSAL) and the Router with it.
        injector.get(ReauthenticationService).reauthenticate();
      }
      return throwError(() => apiError);
    }),
  );
};

function triggersReauthentication(url: string, body: unknown): boolean {
  if (
    !url.startsWith(environment.apiUrl) ||
    url.startsWith(`${environment.apiUrl}/api/auth/login`)
  ) {
    return false;
  }
  // `/api/auth/me` answers 401 two ways. A token the API rejects is answered by the authentication
  // middleware with no body: the session is gone, so sign in again. A valid token with no user row
  // behind it is answered by AuthController with an ErrorResponse: signing out would not help, and
  // the bootstrap's fallback to POST /api/auth/login creates the row.
  if (
    url.startsWith(`${environment.apiUrl}/api/auth/me`) &&
    isErrorEnvelope(body)
  ) {
    return false;
  }
  return true;
}

function isErrorEnvelope(body: unknown): boolean {
  return (
    !!body &&
    typeof body === 'object' &&
    typeof (body as { statusCode?: unknown }).statusCode === 'number'
  );
}
